import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import AuthModal from '@/components/AuthModal';
import type { Entitlements } from '@/services/authService';
import { billingService, MANAGE_SUBSCRIPTIONS_URL } from '@/services/billingService';
import { isNativeApp } from '@/utils/platform';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeleted: () => void;
  /**
   * The account's current plan, so the dialog can warn about a subscription
   * that deleting will not stop. Null while unknown; the warning is then
   * omitted rather than guessed at.
   */
  entitlements?: Entitlements | null;
}

/**
 * Whether deleting this account would leave an App Store subscription
 * billing with nothing to bill for. Apple keeps charging until the customer
 * cancels — nothing on our side can cancel for them — so the dialog's job is
 * to say so and put the cancel one tap away. `canceled` is already not
 * renewing; `expired` has stopped.
 */
export const hasAppleSubscriptionThatWillKeepBilling = (
  entitlements: Entitlements | null | undefined,
): boolean =>
  entitlements?.provider === 'apple' &&
  (entitlements.status === 'active' || entitlements.status === 'past_due');

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  onDeleted,
  entitlements = null,
}) => {
  const { theme } = useTheme();
  const { deleteAccount } = useAuth();
  const isDarkMode = theme === 'dark';

  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isManaging, setIsManaging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const billingWarning = hasAppleSubscriptionThatWillKeepBilling(entitlements);

  useEffect(() => {
    setConfirmText('');
    setIsDeleting(false);
    setIsManaging(false);
    setError(null);
  }, [isOpen]);

  const handleConfirm = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteAccount();
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Account deletion failed');
      setIsDeleting(false);
    }
  };

  const handleManage = async () => {
    setIsManaging(true);
    try {
      await billingService.manageSubscriptions();
    } finally {
      setIsManaging(false);
    }
  };

  const mutedText = isDarkMode ? 'text-gray-300' : 'text-gray-600';

  return (
    <AuthModal isOpen={isOpen} onClose={isDeleting ? () => {} : onClose}>
      <div className={isDarkMode ? 'text-white' : 'text-gray-900'}>
        <h2 className="text-xl font-semibold mb-4">Delete account</h2>
        <p className={`mb-4 text-sm ${mutedText}`}>
          This permanently deletes your account, including your listening progress,
          conversations, and preferences. Deletion is immediate and cannot be undone.
        </p>

        {billingWarning && (
          <div
            role="alert"
            className={`mb-4 rounded-lg border p-3 text-sm ${
              isDarkMode
                ? 'border-amber-500/60 bg-amber-900/20 text-amber-100'
                : 'border-amber-400 bg-amber-50 text-amber-900'
            }`}
          >
            <p className="font-medium mb-1">
              Your {entitlements?.tier_name} subscription will keep billing.
            </p>
            <p className="mb-2">
              Deleting your account does not cancel a subscription bought through the App
              Store. Apple will keep charging your Apple ID until you cancel it yourself, and
              there will be no account for it to apply to. Cancel it first.
            </p>
            {isNativeApp() ? (
              <button
                type="button"
                onClick={() => void handleManage()}
                disabled={isDeleting || isManaging}
                className="font-medium underline underline-offset-2 cursor-pointer disabled:opacity-60"
              >
                {isManaging ? 'Opening…' : 'Manage subscription'}
              </button>
            ) : (
              <p>
                On your iPhone, go to Settings → your name → Subscriptions, or open{' '}
                <a
                  href={MANAGE_SUBSCRIPTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline underline-offset-2"
                >
                  your App Store subscriptions
                </a>
                .
              </p>
            )}
          </div>
        )}

        <label
          htmlFor="delete-account-confirm"
          className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
        >
          Type DELETE to confirm
        </label>
        <input
          id="delete-account-confirm"
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          disabled={isDeleting}
          autoFocus
          autoComplete="off"
          className={`w-full px-3 py-2 mb-4 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
            isDarkMode
              ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-500'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
          }`}
        />

        {error && (
          <p className="mb-4 text-sm text-red-500">{error}</p>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer border ${
              isDarkMode
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
            } ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmText !== 'DELETE' || isDeleting}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Deleting…' : billingWarning ? 'Delete anyway' : 'Delete account'}
          </button>
        </div>
      </div>
    </AuthModal>
  );
};

export default DeleteAccountModal;
